import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControl,
  FormControlLabel, Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const editable = data => ({ enabled: data.enabled, environment: data.environment,
  sandbox: { ...data.sandbox, client_secret: '' }, live: { ...data.live, client_secret: '' } });

export default function PayPalSettingsPanel({ aziendaId, companyName }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const admin = user?.ruolo === 'admin';
  const endpoint = `/paypal/companies/${aziendaId}`;
  const accept = useCallback(data => { setConfig(data); setForm(editable(data)); setDirty(false); }, []);
  const load = useCallback(async () => {
    setBusy(true);
    try { accept((await api.get(endpoint)).data.data); setMessage(null); }
    catch (error) { setMessage({ severity: 'error', text: error.response?.data?.message || 'Configurazione PayPal non disponibile.' }); }
    finally { setBusy(false); }
  }, [endpoint, accept]);
  useEffect(() => { if (admin && aziendaId) load(); }, [admin, aziendaId, load]);
  if (!admin || !aziendaId) return null;
  const update = values => { setForm(previous => ({ ...previous, ...values })); setDirty(true); setMessage(null); };
  const updateCredentials = values => update({ [form.environment]: { ...form[form.environment], ...values } });
  const save = async () => {
    setBusy(true);
    const payload = { enabled: form.enabled, environment: form.environment, updated_at: config.updated_at };
    for (const env of ['sandbox', 'live']) payload[env] = { client_id: form[env].client_id,
      webhook_id: form[env].webhook_id,
      ...(form[env].client_secret.trim() ? { client_secret: form[env].client_secret.trim() } : {}) };
    try {
      accept((await api.put(endpoint, payload)).data.data);
      setMessage({ severity: 'success', text: 'Configurazione PayPal salvata per questa azienda.' });
    } catch (error) { setMessage({ severity: 'error', text: error.response?.data?.message || 'Salvataggio non riuscito.' }); }
    finally { setBusy(false); }
  };
  const importCredentials = async () => {
    setBusy(true);
    try {
      accept((await api.post(endpoint + '/import-environment')).data.data);
      setMessage({ severity: 'success', text: 'Credenziali importate. Aggiorna nell’app PayPal il webhook con l’indirizzo di questa azienda e verifica il collegamento.' });
    } catch (error) { setMessage({ severity: 'error', text: error.response?.data?.message || 'Importazione non riuscita.' }); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { setMessage({ severity: 'success', text: (await api.post(endpoint + '/test')).data.message }); }
    catch (error) { setMessage({ severity: 'error', text: error.response?.data?.message || 'Verifica non riuscita.' }); }
    finally { setBusy(false); }
  };
  const copyWebhook = async () => {
    try { await navigator.clipboard.writeText(form[form.environment].webhook_url); setMessage({ severity: 'success', text: 'Indirizzo webhook copiato.' }); }
    catch (_) { setMessage({ severity: 'info', text: 'Seleziona l’indirizzo webhook nel campo e copialo.' }); }
  };
  const hasCredentials = config && ['sandbox', 'live'].some(env => config[env].client_id || config[env].secret_set);
  return <Card sx={{ my: 2 }}><CardContent>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="h6">PayPal · {companyName || config?.company_name}</Typography>
      <Chip label={config?.ready ? 'Attivo' : config?.enabled ? config.server_enabled ? 'Configurazione da completare' : 'Servizio disattivato sul server' : 'Disattivato'} color={config?.ready ? 'success' : 'default'} />
      {busy && <CircularProgress size={20} />}
    </Box>
    {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
    {!form && <Button onClick={load} disabled={busy}>Carica configurazione</Button>}
    {form && <>
      <Typography variant="body2" sx={{ mb: 2 }}>Queste impostazioni valgono per le fatture emesse da questa azienda.</Typography>
      <FormControlLabel control={<Switch checked={form.enabled} disabled={busy} onChange={event => update({ enabled: event.target.checked })} />}
        label="Abilita il pagamento con PayPal per questa azienda" />
      {!config.server_enabled && <Alert severity="info" sx={{ my: 2 }}>Il servizio PayPal del server è disattivato. Puoi preparare la configurazione dell’azienda; il collegamento sarà operativo dopo l’abilitazione del servizio.</Alert>}
      {!!config.server_missing?.length && <Alert severity="warning" sx={{ my: 2 }}>Configurazione server da completare: {config.server_missing.join(', ')}.</Alert>}
      {!config.encryption_ready && <Alert severity="warning" sx={{ my: 2 }}>La cifratura delle credenziali deve essere configurata sul server prima di salvare un Secret.</Alert>}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}><FormControl fullWidth><InputLabel>Ambiente</InputLabel>
          <Select value={form.environment} label="Ambiente" disabled={busy} onChange={event => update({ environment: event.target.value })}>
            <MenuItem value="sandbox">Sandbox · pagamenti di prova</MenuItem><MenuItem value="live">Live · pagamenti reali</MenuItem>
          </Select></FormControl></Grid>
        <Grid item xs={12} md={8}><Alert severity={form.environment === 'sandbox' ? 'warning' : 'info'}>
          {form.environment === 'sandbox' ? 'Le simulazioni non registrano incassi e non modificano il saldo delle fatture.' : 'Gli incassi confermati vengono registrati sulle fatture di questa azienda.'}
        </Alert></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Client ID" value={form[form.environment].client_id}
          autoComplete="off" disabled={busy} onChange={event => updateCredentials({ client_id: event.target.value })} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth type="password" label="Client Secret" autoComplete="new-password"
          value={form[form.environment].client_secret} disabled={busy || !config.encryption_ready}
          helperText={config[form.environment].secret_set ? 'Secret già salvato. Compila per sostituirlo.' : 'Inserisci il Secret dell’app PayPal di questa azienda.'}
          onChange={event => updateCredentials({ client_secret: event.target.value })} /></Grid>
        <Grid item xs={12} md={6}><TextField fullWidth label="Webhook ID" value={form[form.environment].webhook_id}
          disabled={busy} onChange={event => updateCredentials({ webhook_id: event.target.value })} /></Grid>
        <Grid item xs={12}><TextField fullWidth label="Indirizzo webhook per questa azienda" value={form[form.environment].webhook_url}
          InputProps={{ readOnly: true }} onFocus={event => event.target.select()} helperText="Usa questo indirizzo nel webhook dell’app PayPal, nello stesso ambiente selezionato." /></Grid>
      </Grid>
      <Typography variant="body2" sx={{ my: 2 }}>Eventi: {(config.webhook_events || []).join(', ')}.</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={save} disabled={busy || !dirty}>Salva PayPal per questa azienda</Button>
        <Button onClick={test} disabled={busy || dirty}>Verifica collegamento</Button>
        <Button onClick={copyWebhook} disabled={busy}>Copia indirizzo webhook</Button>
        {!hasCredentials && config.import_available && <Button onClick={importCredentials} disabled={busy || dirty || !config.encryption_ready}>
          Importa credenziali {config.import_environment === 'live' ? 'Live' : 'Sandbox'} da Railway</Button>}
      </Box>
    </>}
  </CardContent></Card>;
}
